import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import type { AlternativeMethodCallbacks } from '../../types/verification';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import TryAnotherWay from '../ui/TryAnotherWay';
import Select from '../ui/Select';
import Input from '../ui/Input';

const US_STATES = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
  ['DC', 'District of Columbia'],
] as const;

type KbaPersonalData = {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
};

type KbaPersonalInfoFormProps = AlternativeMethodCallbacks & {
  formData: FormData;
  loading: boolean;
  error: string | null;
  onSubmit: (data: Partial<FormData>) => void;
  methodExhausted?: boolean;
};

export default function KbaPersonalInfoForm({
  formData,
  loading,
  error,
  onSubmit,
  methodExhausted = false,
  onChooseSms,
  onChooseProspect,
}: KbaPersonalInfoFormProps) {
  const { t } = useTranslation('wizard');
  const stateOptions = [
    { value: '', label: t('kbaPersonalInfo.selectStatePlaceholder') },
    ...US_STATES.map(([code, name]) => ({
      value: code,
      label: name
    })),
  ];
  const [personalData, setPersonalData] = useState<KbaPersonalData>({
    firstName: formData.firstName || '',
    lastName: formData.lastName || '',
    address1: formData.address1 || '',
    address2: formData.address2 || '',
    city: formData.city || '',
    state: formData.state || '',
    zipCode: formData.zipCode || '',
  });

  const [zipError, setZipError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 5);
      setPersonalData((prev) => ({ ...prev, [name]: numericValue }));

      if (numericValue.length > 0 && !/^\d{5}$/.test(numericValue)) {
        setZipError(t('kbaPersonalInfo.zipError'));
      } else {
        setZipError(null);
      }
    } else {
      setPersonalData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;
    onSubmit(personalData);
  };

  const isFormValid = () => {
    const required: Array<keyof KbaPersonalData> = [
      'firstName',
      'lastName',
      'address1',
      'city',
      'state',
      'zipCode',
    ];
    return (
      required.every((field) => personalData[field].trim() !== '') &&
      !zipError &&
      /^\d{5}$/.test(personalData.zipCode)
    );
  };

  return (
    <form
      id="kba-personal-info-form"
      name="kba-personal-info-form"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <div className="text-center">
        <Title
          id="kba-personal-info-title"
          name="kba-personal-info-title"
          as="h1"
          className="mb-2"
        >
          {t('kbaPersonalInfo.title')}
        </Title>
        <Description
          id="kba-personal-info-description"
          name="kba-personal-info-description"
        >
          {t('kbaPersonalInfo.description')}
        </Description>
      </div>

      <div className="space-y-4">
        <Input
          id="kba-personal-info-first-name"
          name="firstName"
          value={personalData.firstName}
          onChange={handleChange}
          label={t('kbaPersonalInfo.firstNameLabel')}
          required
        />

        <Input
          id="kba-personal-info-last-name"
          name="lastName"
          value={personalData.lastName}
          onChange={handleChange}
          label={t('kbaPersonalInfo.lastNameLabel')}
          required
        />

        <Input
          id="kba-personal-info-address1"
          name="address1"
          value={personalData.address1}
          onChange={handleChange}
          label={t('kbaPersonalInfo.streetLabel')}
          required
        />

        <Input
          id="kba-personal-info-address2"
          name="address2"
          value={personalData.address2}
          onChange={handleChange}
          label={t('kbaPersonalInfo.aptSuiteLabel')}
          optional
        />

        <Input
          id="kba-personal-info-city"
          name="city"
          value={personalData.city}
          onChange={handleChange}
          label={t('kbaPersonalInfo.cityLabel')}
          required
        />

        <Select
          id="kba-personal-info-state"
          name="state"
          value={personalData.state}
          onChange={handleChange}
          options={stateOptions}
          label={t('kbaPersonalInfo.stateLabel')}
          required
        />

        <Input
          id="kba-personal-info-zip"
          name="zipCode"
          value={personalData.zipCode}
          onChange={handleChange}
          label={t('kbaPersonalInfo.zipLabel')}
          maxLength={5}
          inputMode="numeric"
          pattern="\d{5}"
          error={zipError}
          required
        />
      </div>

      <ErrorMessage message={error} />

      <TryAnotherWay
        show={!!error || methodExhausted}
        onChooseSms={onChooseSms}
        onChooseProspect={onChooseProspect}
      />

      {!methodExhausted && (
        <Button
          id="kba-personal-info-submit"
          name="kba-personal-info-submit"
          type="submit"
          disabled={loading || !isFormValid()}
          loading={loading}
          loadingText={t('kbaPersonalInfo.saving')}
          fullWidth
          size="lg"
        >
          {t('kbaPersonalInfo.submit')}
        </Button>
      )}
    </form>
  );
}
